/**
 * P&ID Object Classification
 *
 * Single source of truth for all P&ID object types used in the application.
 * Mirrors the `PnidObjectHierarchy` tab of Armin's "Hierarchies and Architectures"
 * sheet (the canonical taxonomy; Armin is the final owner). Any change must track
 * that sheet — including its preserved spellings (e.g. "Capilary", "Wier",
 * "Descrete", "Vaccum", "Threeway", "Containted").
 *
 * Level I  — top-level category (Annotation, Equipment, Instrumentation, Piping)
 * Level II — subcategory within Level I
 * Level III — specific object type within Level II (null if not applicable)
 */

export type PnidLevelI = 'Annotation' | 'Equipment' | 'Instrumentation' | 'Piping'

export interface PnidClassificationEntry {
  levelI: PnidLevelI
  levelII: string
  levelIII: string | null
  notes?: string
}

export const PNID_CLASSIFICATION: PnidClassificationEntry[] = [
  // ── Annotation ───────────────────────────────────────────────────────────
  { levelI: 'Annotation', levelII: 'Arrow',     levelIII: 'Annotation Arrow',   notes: 'Diagonal line connecting annotation or attribute to an object' },
  { levelI: 'Annotation', levelII: 'Arrow',     levelIII: 'Arrow Head' },
  { levelI: 'Annotation', levelII: 'Arrow',     levelIII: 'Dimension Arrows',   notes: 'Distance, length, etc.' },
  { levelI: 'Annotation', levelII: 'Arrow',     levelIII: 'Direction Arrow',    notes: 'Direction, slope' },
  { levelI: 'Annotation', levelII: 'Connector', levelIII: 'Attribute Connector', notes: 'e.g. connecting line number change to its node (location)' },
  { levelI: 'Annotation', levelII: 'Connector', levelIII: 'Off Page Connector', notes: 'Chevron' },
  { levelI: 'Annotation', levelII: 'Connector', levelIII: 'Off Plot Connector', notes: 'Chevron' },
  { levelI: 'Annotation', levelII: 'Connector', levelIII: 'Tag Connector' },
  { levelI: 'Annotation', levelII: 'Connector', levelIII: 'Tie-in Symbol',      notes: 'Hexagon' },
  { levelI: 'Annotation', levelII: 'Connector', levelIII: 'To Atmosphere' },
  { levelI: 'Annotation', levelII: 'Connector', levelIII: 'To Closed Drain' },
  { levelI: 'Annotation', levelII: 'Connector', levelIII: 'To Open Drain' },
  { levelI: 'Annotation', levelII: 'Connector', levelIII: 'Utility Connector',  notes: 'Box' },
  { levelI: 'Annotation', levelII: 'Connector', levelIII: 'Jumpover',           notes: 'tilde (~) used to break a line crossing over another' },
  { levelI: 'Annotation', levelII: 'Connector', levelIII: 'Component Connector' },
  { levelI: 'Annotation', levelII: 'Object',    levelIII: 'Angle' },
  { levelI: 'Annotation', levelII: 'Object',    levelIII: 'Framing' },
  { levelI: 'Annotation', levelII: 'Object',    levelIII: 'Ground' },
  { levelI: 'Annotation', levelII: 'Text',      levelIII: 'Attribute',          notes: 'Text: Setting, limit, normal position, fail position, direction, etc.' },
  { levelI: 'Annotation', levelII: 'Text',      levelIII: 'Note',               notes: 'Text' },
  { levelI: 'Annotation', levelII: 'Text',      levelIII: 'Numbering',          notes: 'Circle, triangle, diamond' },
  { levelI: 'Annotation', levelII: 'Text',      levelIII: 'Tag',                notes: 'Text: equipment tag, instrument tag, line number' },
  { levelI: 'Annotation', levelII: 'Text',      levelIII: 'Title Block',        notes: 'Text: Description, Name, Size' },
  { levelI: 'Annotation', levelII: 'Text',      levelIII: 'Stream Name',        notes: 'the stream identifiers in the top header of the material balance table' },
  { levelI: 'Annotation', levelII: 'Text',      levelIII: 'Stream Number',      notes: 'the stream numbers/letters in the top header' },
  { levelI: 'Annotation', levelII: 'Text',      levelIII: 'Property Name',      notes: 'the property names in the left column (temperature, pressure, flow, composition, etc.)' },

  // ── Equipment ─────────────────────────────────────────────────────────────
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Baffle' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Bottom Section' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Head' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Insulation - Equipment' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Manway' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Mid Section' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Shell Side' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Top Section' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Tray' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Tube Side' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Vortex Breaker' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Wier' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Boot' },
  { levelI: 'Equipment', levelII: 'Component',          levelIII: 'Secondary Containment', notes: 'Collector pan / dike / bund under a tank that catches leaks and directs them to drain' },
  { levelI: 'Equipment', levelII: 'Connector',          levelIII: 'Bottoms' },
  { levelI: 'Equipment', levelII: 'Connector',          levelIII: 'Discharge' },
  { levelI: 'Equipment', levelII: 'Connector',          levelIII: 'Feed' },
  { levelI: 'Equipment', levelII: 'Connector',          levelIII: 'Inlet' },
  { levelI: 'Equipment', levelII: 'Connector',          levelIII: 'Nozzle' },
  { levelI: 'Equipment', levelII: 'Connector',          levelIII: 'Outlet' },
  { levelI: 'Equipment', levelII: 'Connector',          levelIII: 'Overhead' },
  { levelI: 'Equipment', levelII: 'Connector',          levelIII: 'Side Draw' },
  { levelI: 'Equipment', levelII: 'Connector',          levelIII: 'Suction' },
  { levelI: 'Equipment', levelII: 'Driver',             levelIII: 'Diesel Engine' },
  { levelI: 'Equipment', levelII: 'Driver',             levelIII: 'Electric Motor' },
  { levelI: 'Equipment', levelII: 'Driver',             levelIII: 'Gas Engine' },
  { levelI: 'Equipment', levelII: 'Driver',             levelIII: 'Turbine' },
  { levelI: 'Equipment', levelII: 'Heat Exchanger',     levelIII: 'Air Cooler',           notes: 'Open equipment, with fins, fans, etc.' },
  { levelI: 'Equipment', levelII: 'Heat Exchanger',     levelIII: 'Double Pipe',          notes: 'Enclosed two separate streams' },
  { levelI: 'Equipment', levelII: 'Heat Exchanger',     levelIII: 'Kettle',               notes: 'Enclosed two separate streams; oversized shell for vapour disengagement. Pick this for non-reboiler service and name the service in the Note' },
  { levelI: 'Equipment', levelII: 'Heat Exchanger',     levelIII: 'Kettle Reboiler',      notes: 'Enclosed two separate streams' },
  { levelI: 'Equipment', levelII: 'Heat Exchanger',     levelIII: 'Plate Exchanger',      notes: 'Enclosed two separate streams' },
  { levelI: 'Equipment', levelII: 'Heat Exchanger',     levelIII: 'Shell Tube Exchanger', notes: 'Enclosed two separate streams' },
  { levelI: 'Equipment', levelII: 'Heat Exchanger',     levelIII: 'Spiral',               notes: 'Enclosed two separate streams' },
  { levelI: 'Equipment', levelII: 'Heater',             levelIII: 'Electric Heater',      notes: 'Typically vessel internal' },
  { levelI: 'Equipment', levelII: 'Heater',             levelIII: 'Fired Heater' },
  { levelI: 'Equipment', levelII: 'Heater',             levelIII: 'Furnace' },
  { levelI: 'Equipment', levelII: 'Heater',             levelIII: 'Heating Tube' },
  { levelI: 'Equipment', levelII: 'Packaged Unit',      levelIII: 'Packaged Unit',        notes: 'Skid package, Vendor supplied system, can be anything' },
  { levelI: 'Equipment', levelII: 'Rotating Equipment', levelIII: 'Centrifugal Blower',   notes: 'Screw, lobe' },
  { levelI: 'Equipment', levelII: 'Rotating Equipment', levelIII: 'Centrifugal Compressor', notes: 'Single or multi-stage, integrally geared' },
  { levelI: 'Equipment', levelII: 'Rotating Equipment', levelIII: 'Centrifugal Pump',     notes: 'Single or multi-stage' },
  { levelI: 'Equipment', levelII: 'Rotating Equipment', levelIII: 'Mixer',                notes: 'Typically vessel internal' },
  { levelI: 'Equipment', levelII: 'Rotating Equipment', levelIII: 'Positive Displacement Pump', notes: 'Gear, screw, reciprocating, diaphragm' },
  { levelI: 'Equipment', levelII: 'Rotating Equipment', levelIII: 'Reciprocating Blower' },
  { levelI: 'Equipment', levelII: 'Rotating Equipment', levelIII: 'Reciprocating Compressor', notes: 'Piston type' },
  { levelI: 'Equipment', levelII: 'Rotating Equipment', levelIII: 'Screw Compressor' },
  { levelI: 'Equipment', levelII: 'Rotating Equipment', levelIII: 'Screw Pump' },
  { levelI: 'Equipment', levelII: 'Rotating Equipment', levelIII: 'Specialty Pump',       notes: 'Canned, sump, vertical' },
  { levelI: 'Equipment', levelII: 'Solids Handling',    levelIII: 'Conveyor',             notes: 'Belt, screw, bucket' },
  { levelI: 'Equipment', levelII: 'Solids Handling',    levelIII: 'Feeder',               notes: 'Rotary, screw feeders' },
  { levelI: 'Equipment', levelII: 'Solids Handling',    levelIII: 'Mill',                 notes: 'Size-reduction equipment' },
  { levelI: 'Equipment', levelII: 'Stack',              levelIII: 'Flare' },
  { levelI: 'Equipment', levelII: 'Stack',              levelIII: 'Vent' },
  { levelI: 'Equipment', levelII: 'Tank',               levelIII: 'Atmospheric Tank',     notes: 'Low pressure, General storage/service tanks' },
  { levelI: 'Equipment', levelII: 'Tank',               levelIII: 'Pit' },
  { levelI: 'Equipment', levelII: 'Tank',               levelIII: 'Pond' },
  { levelI: 'Equipment', levelII: 'Tank',               levelIII: 'Underground Tank' },
  { levelI: 'Equipment', levelII: 'Vessel',             levelIII: 'Column',               notes: 'AKA Tower, Distillation, absorption, stripping, etc.' },
  { levelI: 'Equipment', levelII: 'Vessel',             levelIII: 'Filter',               notes: 'Cartridge, bag, etc' },
  { levelI: 'Equipment', levelII: 'Vessel',             levelIII: 'Pressure Vessel',      notes: 'Drums, separators, accumulators' },
  { levelI: 'Equipment', levelII: 'Vessel',             levelIII: 'Reactor',              notes: 'Generic reactor (CSTR, PFR, etc.)' },

  // ── Instrumentation ───────────────────────────────────────────────────────
  { levelI: 'Instrumentation', levelII: 'Actuator', levelIII: 'Diaphragm' },
  { levelI: 'Instrumentation', levelII: 'Actuator', levelIII: 'Handwheel with Actuator' },
  { levelI: 'Instrumentation', levelII: 'Actuator', levelIII: 'Manual' },
  { levelI: 'Instrumentation', levelII: 'Actuator', levelIII: 'Motor' },
  { levelI: 'Instrumentation', levelII: 'Actuator', levelIII: 'Pilot',                 notes: 'P in a box' },
  { levelI: 'Instrumentation', levelII: 'Actuator', levelIII: 'Piston' },
  { levelI: 'Instrumentation', levelII: 'Actuator', levelIII: 'Pressure Balance Diaphragm' },
  { levelI: 'Instrumentation', levelII: 'Actuator', levelIII: 'Solenoid' },
  { levelI: 'Instrumentation', levelII: 'Digital',  levelIII: 'DCS Instrument',        notes: 'BOXED CIRCLE, DCS' },
  { levelI: 'Instrumentation', levelII: 'Function', levelIII: 'Averaging',             notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Function', levelIII: 'Difference',            notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Function', levelIII: 'Dividing',              notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Function', levelIII: 'Exponential',           notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Function', levelIII: 'High Select',           notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Function', levelIII: 'Low Select',            notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Function', levelIII: 'Multiplying',           notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Function', levelIII: 'Square Root',           notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Function', levelIII: 'Summing',               notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Line',     levelIII: 'Capilary',              notes: 'LINE SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Line',     levelIII: 'Connection to Process', notes: 'Also see Piping/Line/Tube' },
  { levelI: 'Instrumentation', levelII: 'Line',     levelIII: 'Electric Signal',       notes: 'LINE SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Line',     levelIII: 'Electromagnetic',       notes: 'LINE SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Line',     levelIII: 'Fieldbus',              notes: 'LINE SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Line',     levelIII: 'Hydraulic Signal',      notes: 'LINE SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Line',     levelIII: 'Logic Data Software Signal', notes: 'LINE SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Line',     levelIII: 'Mechanical Link',       notes: 'LINE SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Line',     levelIII: 'Pneumatic Signal',      notes: 'LINE SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Line',     levelIII: 'Signal Tee' },
  { levelI: 'Instrumentation', levelII: 'Local',    levelIII: 'Bottle' },
  { levelI: 'Instrumentation', levelII: 'Local',    levelIII: 'Descrete Instrument Backlit', notes: 'CIRCLE NO BOX, Discrete, four lines radiating out' },
  { levelI: 'Instrumentation', levelII: 'Local',    levelIII: 'Discrete Instrument',   notes: 'CIRCLE NO BOX, Discrete' },
  { levelI: 'Instrumentation', levelII: 'Local',    levelIII: 'Hardware Interlock',    notes: 'DIAMOND NO BOX' },
  { levelI: 'Instrumentation', levelII: 'PLC',      levelIII: 'PLC',                   notes: 'BOXED DIAMOND' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Analyzer',              notes: 'A, AI, AT (pH, conductivity, etc.), CIRCLE' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Flow Glass',            notes: 'CIRCLE' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Flow Nozzle',           notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Magnetic Flowmeter',    notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Mass Meter',            notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Orifice in Quick Change Fitting', notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Orifice Plate',         notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Sight Glass',           notes: 'LG, SG, Viewing window, CIRCLE' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Thermowell',            notes: 'TW, Circle' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Ultrasonic Meter',      notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Venturi Meter',         notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Vortex Meter',          notes: 'SYMBOL' },
  { levelI: 'Instrumentation', levelII: 'Sensor',   levelIII: 'Wedge Meter',           notes: 'SYMBOL' },

  // ── Piping ────────────────────────────────────────────────────────────────
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Blind Flange',     notes: 'Line termination' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Cap',              notes: 'Line termination' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Flange',           notes: 'Connecting lines' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Hose Connection',  notes: 'Flexible connection' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'In-line Silencer' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Plate',            notes: 'Spacer inserted' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Plug' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Reducer',          notes: 'Concentric/eccentric' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Reducer Eccentric' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Spacer',           notes: 'Spacer removed / blinded' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Spec Blind Closed', notes: 'Line blocked' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Spec Blind Open',  notes: 'Line blocked' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Tee' },
  { levelI: 'Piping', levelII: 'Fitting',              levelIII: 'Venturi' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Electric Tracing' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Gauge Siphon' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Heat Tracing' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Hose' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Insulation' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Line Number Break' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Pipe' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Service or System Break', notes: 'Logical system separation, for mechanical circuits' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Spec Break',       notes: 'Change in line class/specification (AKA Line Class Break)' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Steam Tracing' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Tube',             notes: 'Also see Instrumentation/Line/Connection to Process' },
  { levelI: 'Piping', levelII: 'Line',                 levelIII: 'Underground Line', notes: 'Line with AG/UG breaks' },
  { levelI: 'Piping', levelII: 'Self Actuated Device', levelIII: 'Low Pressure Vent', notes: 'Pressure/Vacuum Vent' },
  { levelI: 'Piping', levelII: 'Self Actuated Device', levelIII: 'Pressure Relief Valve', notes: 'Spring-loaded PRV/PSV' },
  { levelI: 'Piping', levelII: 'Self Actuated Device', levelIII: 'Regulator - Back Pressure' },
  { levelI: 'Piping', levelII: 'Self Actuated Device', levelIII: 'Regulator - Back Pressure Self Contained' },
  { levelI: 'Piping', levelII: 'Self Actuated Device', levelIII: 'Regulator - Differential Pressure' },
  { levelI: 'Piping', levelII: 'Self Actuated Device', levelIII: 'Regulator - Pressure Reducing' },
  { levelI: 'Piping', levelII: 'Self Actuated Device', levelIII: 'Regulator - Pressure Reducing Self Containted' },
  { levelI: 'Piping', levelII: 'Self Actuated Device', levelIII: 'Rupture Disc Assembly', notes: 'Disc + holder + valve' },
  { levelI: 'Piping', levelII: 'Self Actuated Device', levelIII: 'Vaccum Safety Valve' },
  { levelI: 'Piping', levelII: 'Specialty',            levelIII: 'Chemical Seal' },
  { levelI: 'Piping', levelII: 'Specialty',            levelIII: 'Gooseneck',        notes: 'Passive safety device' },
  { levelI: 'Piping', levelII: 'Specialty',            levelIII: 'Specialty Item' },
  { levelI: 'Piping', levelII: 'Specialty',            levelIII: 'Static Mixer' },
  { levelI: 'Piping', levelII: 'Specialty',            levelIII: 'Strainer',         notes: 'Y-strainer, basket, etc' },
  { levelI: 'Piping', levelII: 'Specialty',            levelIII: 'Trap' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Angle Valve' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Ball Valve',       notes: 'Quarter-turn isolation' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Ball Valve Closed', notes: 'Quarter-turn isolation' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Butterfly Valve',  notes: 'Large-diameter isolation' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Butterfly Valve Closed', notes: 'Large-diameter isolation' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Bypass' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Bypass Closed' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Check Valve',      notes: 'Swing, lift, etc' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Double Disk' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Gate Valve',       notes: 'On/off isolation' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Gate Valve Closed', notes: 'On/off isolation' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Globe Valve',      notes: 'Throttling service' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Globe Valve Closed', notes: 'Throttling service' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Needle Valve',     notes: 'Fine control, drain, etc' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Needle Valve Closed', notes: 'Fine control, drain, etc' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Nonslam Check Valve', notes: 'Swing, lift, etc' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Plug Valve',       notes: 'Lubricated/non-lubricated' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Plug Valve Closed', notes: 'Lubricated/non-lubricated' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Threeway valve' },
  { levelI: 'Piping', levelII: 'Valve',                levelIII: 'Threeway valve Closed' },
]

// ── Node-type resolution ──────────────────────────────────────────────────────

/**
 * Maps a classification selection to the internal graph NodeType.
 * This is the single place that owns this coupling.
 *
 *   Annotation  + Text + Tag   → 'tag'   (Tag is a leaf under Text, not a Level II)
 *   Piping      + Line         → 'connection'
 *   Instrumentation + Line    → 'connection'
 *   Everything else            → 'symbol'
 *
 * `levelIII` is optional: connection detection only needs the Level II, but the
 * tag leaf lives at Level III, so callers that have it (the Add-Object panel, the
 * Type dropdown) should pass it to resolve a tag.
 */
export function resolveNodeType(
  levelI: PnidLevelI,
  levelII: string,
  levelIII?: string | null,
): 'symbol' | 'tag' | 'connection' {
  if (levelI === 'Annotation' && levelII === 'Text' && levelIII === 'Tag') return 'tag'
  if (levelI === 'Piping' && levelII === 'Line') return 'connection'
  if (levelI === 'Instrumentation' && levelII === 'Line') return 'connection'
  return 'symbol'
}

/**
 * Reverse-maps a backend-detected node (by its internal type + legacy
 * category string) to the new 3-level classification.  Used to seed the
 * Level I / II / III dropdowns in the Node Details flyout for existing nodes.
 *
 * LevelIII is intentionally left empty here — it cannot be reliably inferred
 * from legacy category strings and must be set by the user.
 */
export function resolveClassification(
  nodeType: 'symbol' | 'tag' | 'connection',
  category?: string,
): { levelI: PnidLevelI; levelII: string; levelIII: string } {
  if (nodeType === 'tag') {
    // The sheet moved the tag leaf under Annotation ▸ Text (was Annotation ▸ Tag ▸ ID).
    return { levelI: 'Annotation', levelII: 'Text', levelIII: 'Tag' }
  }
  if (nodeType === 'connection') {
    // 'Signal' is the legacy category for instrumentation lines; all other
    // connection nodes default to piping lines.
    if (category?.trim() === 'Signal') {
      return { levelI: 'Instrumentation', levelII: 'Line', levelIII: '' }
    }
    return { levelI: 'Piping', levelII: 'Line', levelIII: '' }
  }

  // Symbol — map from legacy category name. Every target levelII below MUST exist
  // in the sheet's PnidObjectHierarchy (above) or the seeded dropdown goes blank.
  const OLD_CATEGORY_TO_LEVEL: Record<string, { levelI: PnidLevelI; levelII: string }> = {
    'Valve':                { levelI: 'Piping',          levelII: 'Valve' },
    'Instrumented Valve':   { levelI: 'Piping',          levelII: 'Valve' },
    'Piping Fitting':       { levelI: 'Piping',          levelII: 'Fitting' },
    'Piping Components':    { levelI: 'Piping',          levelII: 'Fitting' },
    'Piping':               { levelI: 'Piping',          levelII: 'Line' },
    'Piping Endpoint':      { levelI: 'Annotation',      levelII: 'Connector' },
    'Connector Link':       { levelI: 'Annotation',      levelII: 'Connector' },
    'Inlet/Outlet':         { levelI: 'Annotation',      levelII: 'Connector' },
    'Inlet':                { levelI: 'Annotation',      levelII: 'Connector' },
    'Pump':                 { levelI: 'Equipment',       levelII: 'Rotating Equipment' },
    'Compressor':           { levelI: 'Equipment',       levelII: 'Rotating Equipment' },
    'Mixer':                { levelI: 'Equipment',       levelII: 'Rotating Equipment' },
    'Centrifuge':           { levelI: 'Equipment',       levelII: 'Rotating Equipment' },
    'Vessel':               { levelI: 'Equipment',       levelII: 'Vessel' },
    'Filter':               { levelI: 'Equipment',       levelII: 'Vessel' },
    'Heat Exchanger':       { levelI: 'Equipment',       levelII: 'Heat Exchanger' },
    'Tube Bundle':          { levelI: 'Equipment',       levelII: 'Heat Exchanger' },
    'Motor':                { levelI: 'Equipment',       levelII: 'Driver' },
    'Generator':            { levelI: 'Equipment',       levelII: 'Driver' },
    'Equipment':            { levelI: 'Equipment',       levelII: 'Packaged Unit' },
    'Peripheral Equipment': { levelI: 'Equipment',       levelII: 'Packaged Unit' },
    'Mechanical Equipment': { levelI: 'Equipment',       levelII: 'Packaged Unit' },
    'Dryer':                { levelI: 'Equipment',       levelII: 'Packaged Unit' },
    'Crusher':              { levelI: 'Equipment',       levelII: 'Solids Handling' },
    // Sheet keeps discrete instruments under 'Local' and DCS/PLC under 'Digital'
    // (there is no 'Discrete Instrument' / 'DCS Instrument' levelII any more).
    'Instrument':           { levelI: 'Instrumentation', levelII: 'Local' },
    'Instrument Indicator': { levelI: 'Instrumentation', levelII: 'Digital' },
    'Signal':               { levelI: 'Instrumentation', levelII: 'Line' },
    // YOLO's original label for instrument bubbles (the KG's lowercase
    // 'valve'/'piping'/'instrument'/'equipment' are covered case-insensitively
    // by the entries above).
    'Instrumentation':      { levelI: 'Instrumentation', levelII: 'Local' },
  }

  // Case-insensitive lookup: the legacy names are Title-Case but today's KG
  // emits lowercase categories — a case-sensitive miss used to fall through to
  // the Equipment default, rendering e.g. a drain valve as "Equipment" while
  // the agent read the same node's data as a valve.
  const wanted = (category?.trim() ?? '').toLowerCase()
  const matchKey = Object.keys(OLD_CATEGORY_TO_LEVEL).find((k) => k.toLowerCase() === wanted)
  const mapped = matchKey ? OLD_CATEGORY_TO_LEVEL[matchKey] : undefined
  return mapped
    ? { ...mapped, levelIII: '' }
    : { levelI: 'Equipment', levelII: '', levelIII: '' }
}

/**
 * Forward-map a picked classification (Level I + II) to the raw-vision `category`
 * the KG generators classify on. BOTH the classic generator (`parse.ts`
 * `symbolCategory` → bucketing + instrument/equipment rollups) and the db_ask emit
 * (`emit-raw-tables.ts` → `raw/symbol.category`) key ONLY on `symbols[].category`;
 * `pnid_level_*` and `is_instrument` drive no classification. A manually-added
 * symbol must therefore carry a `category`, or it emits as a bare STRUCTURAL node
 * (no tag/label, absent from the rollups) and lands `category=null` in `raw/` —
 * invisible to the agent. This mirrors the existing junction stamp (a placed point
 * gets `category:"junction"` for the same reason).
 *
 * Generic P&ID taxonomy → generic vision categories (`instrument`/`valve`/
 * `equipment`/`exchanger`/`vessel`), NOT tenant-specific — the forward inverse of
 * `resolveClassification`'s `OLD_CATEGORY_TO_LEVEL`. Returns null for classes that
 * are not a classified symbol (Annotation text/tags; Piping/Instrumentation Lines,
 * which resolve to a tag/connection, not a category-bearing symbol).
 */
export function resolveVisionCategory(levelI: PnidLevelI, levelII?: string | null): string | null {
  switch (levelI) {
    case 'Instrumentation':
      return levelII === 'Line' ? null : 'instrument'
    case 'Piping':
      return levelII === 'Valve' ? 'valve' : null
    case 'Equipment':
      if (levelII === 'Heat Exchanger') return 'exchanger'
      if (levelII === 'Vessel') return 'vessel'
      return 'equipment'
    default:
      return null // Annotation — tags/text, not a classified symbol
  }
}

/**
 * The Level-I a raw-vision `category` implies — inverse of {@link resolveVisionCategory}.
 * Used to decide (NodeDetailsPanelV2) whether a human genuinely reclassified a DETECTED
 * symbol's TOP-LEVEL class: only when the picked Level-I differs from the vision
 * category's implied Level-I do we overwrite `category`, so we never COARSEN a finer
 * vision category (e.g. `pump`) into our generic one (`equipment`) on a same-class edit.
 * Unknown category → null (no implied class).
 */
export function visionCategoryToLevelI(category?: string | null): PnidLevelI | null {
  const c = (category ?? '').trim().toLowerCase()
  if (!c) return null
  if (c === 'instrument' || c === 'instrumentation') return 'Instrumentation'
  if (c === 'valve') return 'Piping'
  if (['equipment', 'exchanger', 'vessel', 'pump', 'drum', 'column', 'tank', 'pressure_vessel', 'compressor'].includes(c))
    return 'Equipment'
  return null
}

/**
 * A raw-vision `symbol_type` for a manually-added symbol, from the picked Level-III.
 * db_ask derives `dexpi_class` from `symbols[].type`, so without this a manual add is
 * reachable only by the coarse `category`, never by fine subtype (gate vs ball valve).
 * Valve leaves map to the `valve_<kind>` tokens the generator uses; other classes have
 * no stable fine token yet → null (category still classifies them).
 */
const VALVE_TYPE: Record<string, string> = {
  'Gate Valve': 'valve_gate',
  'Ball Valve': 'valve_ball',
  'Globe Valve': 'valve_globe',
  'Butterfly Valve': 'valve_butterfly',
  'Check Valve': 'valve_check',
  'Plug Valve': 'valve_plug',
  'Needle Valve': 'valve_needle',
}

export function resolveVisionType(levelIII?: string | null): string | null {
  return VALVE_TYPE[(levelIII ?? '').trim()] ?? null
}

// ── Derived helpers ───────────────────────────────────────────────────────────

/** All distinct Level I categories, in display order. */
export const PNID_LEVEL_I_VALUES: PnidLevelI[] = [
  'Annotation',
  'Equipment',
  'Instrumentation',
  'Piping',
]

/**
 * The one Level-I category exempt from needing a Subcategory/Type to be approved. Annotation
 * objects are kept by the GT export even without a leaf Type, so they may be approved with
 * only a Category set. MUST stay in sync with labelledExport.ts `ANNOTATION_CATEGORY`.
 */
export const ANNOTATION_LEVEL_I: PnidLevelI = 'Annotation'

/**
 * Level-I categories a node of the given type may be classified as. A tag/text node is
 * ALWAYS an Annotation element (Text/Tag/Note/Numbering/…) — it cannot be Equipment,
 * Instrumentation, or Piping, which are SYMBOL categories the generators type from
 * `symbols[].category`/`symbol_type`. So classifying a tag is confined to the Annotation
 * branch: reclassifying a tag's floating label to "Equipment" is a no-op the generator
 * ignores (it types the instrument from its symbol + ISA tag name), which silently
 * misled annotators into thinking they'd re-typed the equipment (#2). To reclassify the
 * equipment, select its SYMBOL glyph, not the label. Symbols/connections keep all four.
 *
 * A `table_cell` is confined the same way: a table cell is text (a stream number, a property
 * name, a value), so Annotation is its only valid branch.
 */
export function categoryOptionsForNodeType(nodeType: string): PnidLevelI[] {
  // Return a fresh array — never hand out the module-level `PNID_LEVEL_I_VALUES`
  // reference (a caller sorting/pushing it would corrupt the shared constant).
  return nodeType === 'tag' || nodeType === 'table_cell'
    ? [ANNOTATION_LEVEL_I]
    : [...PNID_LEVEL_I_VALUES]
}

/**
 * The Level-I category that designates a symbol as an instrument (ENG-2622). The
 * Node Details panel derives `is_instrument` — and therefore the instrument-field
 * visibility + the purple canvas colour — from the annotator's Category selection
 * matching this value, so the display reflects the annotator's choice rather than
 * the model's raw `is_instrument` prediction.
 */
export const INSTRUMENTATION_LEVEL_I: PnidLevelI = 'Instrumentation'

/** All distinct Level II values for a given Level I. */
export function getLevelII(levelI: PnidLevelI): string[] {
  const seen = new Set<string>()
  return PNID_CLASSIFICATION
    .filter((e) => e.levelI === levelI)
    .map((e) => e.levelII)
    .filter((v) => {
      if (seen.has(v)) return false
      seen.add(v)
      return true
    })
}

/** All distinct Level III values for a given Level I + Level II. Null entries excluded. */
export function getLevelIII(levelI: PnidLevelI, levelII: string): string[] {
  return PNID_CLASSIFICATION
    .filter((e) => e.levelI === levelI && e.levelII === levelII && e.levelIII !== null)
    .map((e) => e.levelIII as string)
}

/**
 * True when an object is missing a *required* classification field for approval — i.e. it is
 * NOT yet complete enough to approve / survive the GT export, and the annotator can still fix it.
 * This is the single gate for the Node Details + Add Object "Approve" buttons (a hard block).
 *
 * Required fields, with exceptions:
 *   - Category is always required.
 *   - Subcategory + Type are required EXCEPT for the Annotation category (kept by the export
 *     without a leaf Type) and for (Category, Subcategory) pairs that legitimately have NO
 *     selectable leaf Type (`getLevelIII(...).length === 0`). For those there is no Type to
 *     pick, so requiring one would be an unsatisfiable block.
 *
 * Returns true (block approve):
 *   - unclassified / partially classified (no Category, or no Subcategory on a non-Annotation);
 *   - a (Category, Subcategory) that DOES offer leaf Types but none is chosen.
 * Returns false (allow approve): a leaf Type is set; OR Category is Annotation; OR a
 *   (Category, Subcategory) with no selectable leaf Type.
 */
export function isMissingRequiredType(category: string, subcategory: string, type: string): boolean {
  // Trim internally so the contract is self-contained — a caller that forgets to pre-trim
  // can't slip a whitespace-only value past the check (matches the export's trimOrEmpty).
  const cat = category.trim()
  const sub = subcategory.trim()
  if (type.trim()) return false // has a leaf Type → complete
  if (cat === ANNOTATION_LEVEL_I) return false // Annotation needs only a Category
  // Fully classified to a (cat, sub) that offers no leaf Type → nothing to pick, so never block.
  if (cat && sub && getLevelIII(cat as PnidLevelI, sub).length === 0) return false
  return true
}

/** Look up the notes for a specific entry. */
export function getEntryNotes(
  levelI: PnidLevelI,
  levelII: string,
  levelIII: string | null,
): string | undefined {
  return PNID_CLASSIFICATION.find(
    (e) => e.levelI === levelI && e.levelII === levelII && e.levelIII === levelIII,
  )?.notes
}
